import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Wrench, Zap, Paintbrush, Shield, Award, Users } from 'lucide-react';

const Home: React.FC = () => {
  const services = [
    {
      icon: <Wrench className="h-8 w-8" />,
      title: 'Mecânica Geral',
      description: 'Troca de óleo, revisão geral, manutenção preventiva e corretiva',
      items: ['Troca de óleo', 'Revisão geral', 'Manutenção preventiva', 'Manutenção corretiva']
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: 'Performance',
      description: 'Preparação de carros turbos e aspirados, remap e threestep',
      items: ['Turbo', 'Aspirado', 'Remap', 'Threestep']
    },
    {
      icon: <Paintbrush className="h-8 w-8" />,
      title: 'Funilaria',
      description: 'Polimento, pequenos reparos, projetos e martelinho de ouro',
      items: ['Polimento', 'Pequenos reparos', 'Projetos', 'Martelinho de ouro']
    }
  ];

  const brands = [
    'Volkswagen', 'Fiat', 'Chevrolet', 'Ford', 'Honda', 'Toyota', 
    'Nissan', 'Hyundai', 'Peugeot', 'Renault', 'Citroën', 'Audi',
    'BMW', 'Mercedes-Benz', 'Volvo', 'Mitsubishi'
  ];

  const differentials = [
    {
      icon: <Shield className="h-12 w-12" />,
      title: 'Laudo AVCB Aprovado',
      description: 'Possuímos todas as condições de segurança contra incêndio'
    },
    {
      icon: <Award className="h-12 w-12" />,
      title: 'Mais de 20 Anos',
      description: 'Experiência consolidada no mercado automotivo'
    },
    {
      icon: <Users className="h-12 w-12" />,
      title: 'Equipe Especializada',
      description: 'Profissionais qualificados em carros nacionais e importados'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 text-yellow-400 fill-current" />
              ))}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              REFERÊNCIA EM PREPARAÇÃO DE
              <span className="block text-yellow-400">CARROS TURBOS EM SP</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto leading-relaxed">
              A Will Performance é uma Oficina Mecânica localizada na região da Zona Leste de São Paulo, 
              que atua há mais de 20 anos como oficina mecânica. Hoje, com uma estrutura de mais de 600m², 
              contamos com uma equipe de profissionais especializada em Automóveis Nacionais e Importados.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/sobre"
                className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                Saiba Mais
              </Link>
              <a
                href="https://api.whatsapp.com/send?phone=551123593715"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                Fale Conosco
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nossos Serviços
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Em nossa Oficina Mecânica você encontrará toda a estrutura necessária 
              para a realização dos serviços de manutenção que seu veículo precisa.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-primary-600 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center text-gray-700 group-hover:text-gray-800 transition-colors duration-300">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 group-hover:scale-125 transition-transform duration-300"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/servicos"
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Ver Todos os Serviços
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                "Um novo conceito em centro automotivo!"
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Temos muita satisfação em atendê-lo, além de cuidar de seu carro com qualidade e preços justos.
                </p>
                <p>
                  Contamos também com uma equipe especializada para Carros Turbos, desde manutenção simples, 
                  até instalação completa de Turbos e Carros de Arrancadas.
                </p>
                <p>
                  Trabalhamos com as melhores marcas do mercado e oferecemos uma mão de obra diferenciada 
                  e qualificada para quem quer o MELHOR para seu carro seja nacional ou importado.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {differentials.map((item, index) => (
                <div key={index} className="flex items-start space-x-4 p-6 bg-gray-50 rounded-lg">
                  <div className="text-primary-600 flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nacionais e Importados
            </h2>
            <p className="text-xl text-gray-600">
              Preparação e Manutenção das melhores marcas
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 justify-items-center">
            {brands.map((brand, index) => (
              <div key={index} className="w-full max-w-[140px] bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center justify-center group">
                <div className="w-12 h-12 sm:w-14 sm:h-14 mb-2 flex items-center justify-center bg-gray-100 rounded-full group-hover:bg-primary-50 transition-colors duration-300">
                  <svg className="w-8 h-8 sm:w-9 sm:h-9 text-gray-600 group-hover:text-primary-600 transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L2 7v10c0 5.55 3.84 9.739 9 11 5.16-1.261 9-5.45 9-11V7l-10-5z"/>
                  </svg>
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-700 group-hover:text-primary-600 transition-colors duration-300 text-center leading-tight">{brand}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto para cuidar do seu carro?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Entre em contato conosco e agende uma visita. Contamos com guincho 
            para trazer seu veículo até nossa oficina.
          </p>
          <a
            href="https://api.whatsapp.com/send?phone=551123593715"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200"
          >
            Fale Conosco pelo WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
